import { useCallback, useEffect, useMemo, useState } from "react"
import { Trans, useLingui } from "@lingui/react/macro"
import { getCsrfToken } from "@/lib/csrf"
import { camelizeKeys } from "@/lib/utils"
import {
  SearchIcon,
  ChevronRightIcon,
  PlusIcon,
  LinesIcon,
  BriefcaseIcon,
  CheckIcon,
  CloseIcon,
} from "./icons"
import { AfterCallSubviewHeader } from "./after-call-subview-header"
import type {
  CallDealPipeline,
  CallDealStage,
  CallDealsResponse,
  VoiceCallDeal,
  VoiceCallRecord,
} from "@/types/v2/voice"

// The post-call card's "Link a deal" sub-view (CON-158,
// frames 24/25/26). Reached from the no-deal card's quiet
// "+ Link a deal" affordance. Fetches the call contact's open
// deals (the set the outcome's deal_id validates against) plus
// the pipelines a new deal can be created in. The rep either
// picks an existing deal or creates one for the contact without
// leaving the dark widget; either way `onLink` hands the chosen
// deal back so the card logs the outcome against it on Done.
export function LinkDeal({
  voiceCall,
  contactName,
  currentDeal = null,
  pickTitle = "Link a deal",
  onBack,
  onLink,
  onClear,
}: {
  voiceCall: VoiceCallRecord
  contactName: string
  // The deal currently linked (AI-derived or rep-picked), when
  // changing rather than linking from scratch. Pre-selected in
  // the list and prepended if it isn't among the contact's open
  // deals (e.g. a closed conversation deal). (CON-170)
  currentDeal?: VoiceCallDeal | null
  // Header copy: "Link a deal" by default, "Change deal" when
  // swapping an existing one.
  pickTitle?: string
  onBack: () => void
  onLink: (deal: VoiceCallDeal) => void
  // Unlink the current deal (clear → outcome posts deal_id:
  // null). Only wired when there's a deal to remove. (CON-170)
  onClear?: () => void
}) {
  const { t } = useLingui()
  const firstName = contactName.trim().split(/\s+/)[0] || contactName

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deals, setDeals] = useState<VoiceCallDeal[]>([])
  const [pipelines, setPipelines] = useState<CallDealPipeline[]>([])

  const [mode, setMode] = useState<"pick" | "create">("pick")
  const [search, setSearch] = useState("")

  const [newName, setNewName] = useState("")
  const [pipelineId, setPipelineId] = useState<number | null>(null)
  const [stageId, setStageId] = useState<number | null>(null)
  const [editingStage, setEditingStage] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const response = await fetch(
          `/v2/channels/voice/calls/${voiceCall.id}/deals`,
          {
            headers: { Accept: "application/json" },
            credentials: "same-origin",
          },
        )
        if (!response.ok) throw new Error("deals")
        // Raw `render json:` endpoint — camelize keys the same
        // way `useChannel` does (string values left intact).
        const data = camelizeKeys(await response.json()) as CallDealsResponse
        if (cancelled) return
        setDeals(data.deals ?? [])
        setPipelines(data.pipelines ?? [])
        const firstPipeline = data.pipelines?.[0] ?? null
        setPipelineId(firstPipeline?.id ?? null)
        setStageId(firstPipeline?.stages?.[0]?.id ?? null)
      } catch {
        if (!cancelled) {
          setError(t`We couldn't load the deals. Please try again.`)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [voiceCall.id])

  // The contact's open deals, with the currently-linked deal
  // guaranteed present (prepended if the fetch didn't include
  // it — e.g. a closed conversation deal) so "Change deal"
  // always shows what's linked today, marked as current.
  const displayDeals = useMemo(() => {
    if (!currentDeal) return deals
    if (deals.some((d) => d.id === currentDeal.id)) return deals
    return [currentDeal, ...deals]
  }, [deals, currentDeal])

  const filteredDeals = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return displayDeals
    return displayDeals.filter((d) => d.name.toLowerCase().includes(q))
  }, [displayDeals, search])

  const selectedPipeline = useMemo(
    () => pipelines.find((p) => p.id === pipelineId) ?? null,
    [pipelines, pipelineId],
  )
  // The stage the new deal lands in — defaults to the
  // pipeline's first stage but the rep can change it.
  const selectedStage = useMemo(() => {
    const stages = selectedPipeline?.stages ?? []
    return stages.find((s) => s.id === stageId) ?? stages[0] ?? null
  }, [selectedPipeline, stageId])

  // Switching pipeline re-defaults the stage to that
  // pipeline's first (stage ids don't carry across pipelines).
  const choosePipeline = useCallback(
    (id: number) => {
      setPipelineId(id)
      const next = pipelines.find((p) => p.id === id)
      setStageId(next?.stages?.[0]?.id ?? null)
    },
    [pipelines],
  )

  const createDeal = useCallback(async () => {
    if (submitting || newName.trim().length === 0 || !selectedStage) return
    setSubmitting(true)
    setError(null)
    try {
      const response = await fetch(
        `/v2/channels/voice/calls/${voiceCall.id}/deals`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-CSRF-Token": getCsrfToken(),
          },
          body: JSON.stringify({
            name: newName.trim(),
            stage_id: selectedStage.id,
          }),
          credentials: "same-origin",
        },
      )
      if (!response.ok) throw new Error("create")
      const data = camelizeKeys(await response.json()) as {
        deal: VoiceCallDeal
      }
      onLink(data.deal)
    } catch {
      setError(t`We couldn't create that deal. Please try again.`)
    } finally {
      setSubmitting(false)
    }
  }, [submitting, newName, selectedStage, voiceCall.id, onLink])

  return (
    <div
      data-testid="after-call-link-deal"
      className="flex flex-col gap-3.5 px-[18px] pb-4 pt-[18px] text-[#FBF7F0]"
    >
      <AfterCallSubviewHeader
        title={mode === "create" ? t`New deal` : pickTitle}
        subtitle={t`for ${firstName}`}
        onBack={
          mode === "create" && displayDeals.length > 0
            ? () => setMode("pick")
            : onBack
        }
      />

      {loading ? (
        <p className="text-sm text-[#9A938A]"><Trans>Loading deals…</Trans></p>
      ) : error && displayDeals.length === 0 && mode === "pick" ? (
        <p role="alert" className="text-sm text-[#9A938A]">
          {error}
        </p>
      ) : mode === "create" ? (
        <CreateStep
          firstName={firstName}
          name={newName}
          onNameChange={setNewName}
          pipelines={pipelines}
          selectedPipeline={selectedPipeline}
          selectedStage={selectedStage}
          editing={editingStage}
          onToggleEditing={() => setEditingStage((v) => !v)}
          onChoosePipeline={choosePipeline}
          onChooseStage={(id) => {
            setStageId(id)
            setEditingStage(false)
          }}
          submitting={submitting}
          error={error}
          onCreate={() => void createDeal()}
        />
      ) : displayDeals.length === 0 ? (
        <EmptyState
          firstName={firstName}
          onNewDeal={() => setMode("create")}
        />
      ) : (
        <PickStep
          firstName={firstName}
          search={search}
          onSearchChange={setSearch}
          deals={filteredDeals}
          currentDealId={currentDeal?.id ?? null}
          onPick={onLink}
          onClear={onClear}
          onNewDeal={() => setMode("create")}
        />
      )}
    </div>
  )
}

// Frame 24: search + the contact's deals + a quiet "New deal".
// In "change" mode it also flags the currently-linked deal and
// offers a "Don't link a deal" clear row (CON-170).
function PickStep({
  firstName,
  search,
  onSearchChange,
  deals,
  currentDealId,
  onPick,
  onClear,
  onNewDeal,
}: {
  firstName: string
  search: string
  onSearchChange: (value: string) => void
  deals: VoiceCallDeal[]
  currentDealId: number | null
  onPick: (deal: VoiceCallDeal) => void
  onClear?: () => void
  onNewDeal: () => void
}) {
  const { t } = useLingui()
  return (
    <>
      <p className="text-xs text-[#9A938A]">
        <Trans>Log this call against a deal — follow-ups move with it.</Trans>
      </p>

      <div className="flex items-center gap-2 rounded-[10px] border border-[#3A352E] bg-[#252019] px-[11px] py-[9px]">
        <SearchIcon width={14} height={14} className="shrink-0 text-[#6E685F]" />
        <input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          aria-label={t`Search deals`}
          placeholder={t`Search deals…`}
          className="grow bg-transparent text-[13px] text-[#FBF7F0] placeholder:text-[#6E685F] focus:outline-none"
        />
      </div>

      {/* Unlink the current deal — only in change mode. */}
      {onClear ? (
        <button
          type="button"
          onClick={onClear}
          data-testid="link-deal-clear"
          className="flex items-center gap-2.5 rounded-[11px] border border-[#3A352E] px-3 py-2.5 text-left text-[13px] text-[#D9D3C8] hover:bg-[#2A2620]"
        >
          <CloseIcon width={13} height={13} className="shrink-0 text-[#9A938A]" />
          <Trans>Don't link a deal</Trans>
        </button>
      ) : null}

      <div className="flex flex-col gap-[7px]">
        <span className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#9A938A]">
          {t`${firstName}'s deals`}
        </span>
        {deals.length === 0 ? (
          <p className="px-1 py-2 text-[13px] text-[#9A938A]">
            <Trans>No deals match "{search}".</Trans>
          </p>
        ) : (
          deals.map((deal) => {
            const isCurrent = deal.id === currentDealId
            return (
              <button
                key={deal.id}
                type="button"
                onClick={() => onPick(deal)}
                aria-current={isCurrent}
                data-testid="link-deal-option"
                className={`flex items-center gap-2.5 rounded-[11px] border px-3 py-2.5 text-left hover:bg-[#2A2620] ${
                  isCurrent
                    ? "border-[#E0A56A]/50 bg-[#2A2620]"
                    : "border-[#3A352E] bg-[#252019]"
                }`}
              >
                <span className="flex min-w-0 grow flex-col gap-px">
                  <span className="truncate text-[13px] font-medium text-[#FBF7F0]">
                    {deal.name}
                  </span>
                  <span className="truncate text-xs text-[#9A938A]">
                    {isCurrent
                      ? t`Linked now · ${dealSubtitle(deal)}`
                      : dealSubtitle(deal)}
                  </span>
                </span>
                {isCurrent ? (
                  <CheckIcon
                    width={13}
                    height={13}
                    className="shrink-0 text-[#7FD79A]"
                  />
                ) : (
                  <ChevronRightIcon
                    width={7}
                    height={12}
                    className="shrink-0 text-[#6E685F]"
                  />
                )}
              </button>
            )
          })
        )}
        <NewDealLink firstName={firstName} onClick={onNewDeal} />
      </div>
    </>
  )
}

// Frame 25: warm, no-dead-end empty state.
function EmptyState({
  firstName,
  onNewDeal,
}: {
  firstName: string
  onNewDeal: () => void
}) {
  const { t } = useLingui()
  return (
    <>
      <div className="flex flex-col items-center gap-2.5 px-2 pb-1.5 pt-[18px] text-center">
        <span className="flex size-11 items-center justify-center rounded-full bg-[#2A2620]">
          <BriefcaseIcon width={20} height={20} className="text-[#9A938A]" />
        </span>
        <span className="text-sm font-semibold text-[#FBF7F0]">
          {t`No deals with ${firstName} yet`}
        </span>
        <span className="max-w-[248px] text-xs leading-[17px] text-[#9A938A]">
          <Trans>
            No problem — this call's follow-ups track on {firstName}'s contact.
            Link a deal anytime later.
          </Trans>
        </span>
      </div>
      <button
        type="button"
        onClick={onNewDeal}
        data-testid="link-deal-new"
        className="flex items-center justify-center gap-[7px] rounded-[11px] border border-[#3A352E] p-[9px] text-[13px] font-medium text-[#E0A56A] hover:bg-[#2A2620]"
      >
        <PlusIcon width={13} height={13} className="shrink-0" />
        {t`New deal for ${firstName}`}
      </button>
    </>
  )
}

// Frame 26: name the deal (intent first), then the pipeline +
// stage it lands in (defaulted, but changeable), then
// create + link.
function CreateStep({
  firstName,
  name,
  onNameChange,
  pipelines,
  selectedPipeline,
  selectedStage,
  editing,
  onToggleEditing,
  onChoosePipeline,
  onChooseStage,
  submitting,
  error,
  onCreate,
}: {
  firstName: string
  name: string
  onNameChange: (value: string) => void
  pipelines: CallDealPipeline[]
  selectedPipeline: CallDealPipeline | null
  selectedStage: CallDealStage | null
  editing: boolean
  onToggleEditing: () => void
  onChoosePipeline: (id: number) => void
  onChooseStage: (id: number) => void
  submitting: boolean
  error: string | null
  onCreate: () => void
}) {
  const { t } = useLingui()
  return (
    <>
      <div className="flex flex-col gap-[7px]">
        <label
          htmlFor="link-deal-name"
          className="text-[13px] font-semibold text-[#FBF7F0]"
        >
          <Trans>What's the deal about?</Trans>
        </label>
        <input
          id="link-deal-name"
          value={name}
          autoFocus
          onChange={(e) => onNameChange(e.target.value)}
          data-testid="link-deal-name"
          placeholder={t`e.g. ${firstName} — annual renewal`}
          className="rounded-[9px] border-[1.5px] border-[#E0A56A] bg-[#221E18] px-[11px] py-2.5 text-[13px] text-[#FBF7F0] placeholder:text-[#6E685F] focus:outline-none"
        />
      </div>

      {/* Pipeline · stage — defaulted, but the rep can change
          both before creating. */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2 px-px">
          <LinesIcon width={13} height={13} className="shrink-0 text-[#9A938A]" />
          <span className="grow text-xs text-[#9A938A]">
            {selectedPipeline
              ? selectedStage
                ? `${selectedPipeline.name} · ${selectedStage.name}`
                : selectedPipeline.name
              : <Trans>No pipeline available</Trans>}
          </span>
          {selectedPipeline ? (
            <button
              type="button"
              onClick={onToggleEditing}
              data-testid="link-deal-change-stage"
              className="text-[11px] font-medium text-[#9A938A] underline"
            >
              {editing ? <Trans>Done</Trans> : <Trans>Change</Trans>}
            </button>
          ) : null}
        </div>
        {editing && selectedPipeline ? (
          <div className="flex flex-col gap-2 rounded-[10px] border border-[#3A352E] bg-[#252019] p-[7px]">
            {pipelines.length > 1 ? (
              <PickerGroup
                label={t`Pipeline`}
                ariaLabel={t`Pipeline`}
                options={pipelines.map((p) => ({ id: p.id, name: p.name }))}
                selectedId={selectedPipeline.id}
                onChoose={onChoosePipeline}
              />
            ) : null}
            <PickerGroup
              label={t`Stage`}
              ariaLabel={t`Stage`}
              testId="link-deal-stage-option"
              options={selectedPipeline.stages}
              selectedId={selectedStage?.id ?? null}
              onChoose={onChooseStage}
            />
          </div>
        ) : null}
      </div>

      {error ? (
        <p role="alert" className="text-xs text-[#9A938A]">
          {error}
        </p>
      ) : null}

      <button
        type="button"
        onClick={onCreate}
        disabled={submitting || name.trim().length === 0 || !selectedStage}
        data-testid="link-deal-create"
        className="flex items-center justify-center rounded-[11px] bg-[#F4EFE6] px-3.5 py-2.5 text-[13px] font-semibold text-[#1E1B17] hover:bg-[#FBF7F0] disabled:opacity-50"
      >
        {submitting ? <Trans>Creating…</Trans> : <Trans>Create & link</Trans>}
      </button>
    </>
  )
}

// A labelled radio-list used by the New-deal pipeline/stage
// pickers — one captioned group of single-select options.
function PickerGroup({
  label,
  ariaLabel,
  testId,
  options,
  selectedId,
  onChoose,
}: {
  label: string
  ariaLabel: string
  testId?: string
  options: CallDealStage[]
  selectedId: number | null
  onChoose: (id: number) => void
}) {
  return (
    <div className="flex flex-col gap-[3px]">
      <span className="px-[5px] text-[10px] font-semibold uppercase tracking-[0.04em] text-[#6E685F]">
        {label}
      </span>
      <div role="listbox" aria-label={ariaLabel} className="flex flex-col gap-[3px]">
        {options.map((opt) => (
          <button
            key={opt.id}
            type="button"
            role="option"
            aria-selected={opt.id === selectedId}
            onClick={() => onChoose(opt.id)}
            data-testid={testId}
            className={`flex items-center gap-2 rounded-[7px] px-[9px] py-[7px] text-left text-xs ${
              opt.id === selectedId
                ? "bg-[#2F2A24] font-semibold text-[#FBF7F0]"
                : "text-[#D9D3C8]"
            }`}
          >
            <span className="grow">{opt.name}</span>
            {opt.id === selectedId ? (
              <CheckIcon width={12} height={12} className="text-[#7FD79A]" />
            ) : null}
          </button>
        ))}
      </div>
    </div>
  )
}

function NewDealLink({
  firstName,
  onClick,
}: {
  firstName: string
  onClick: () => void
}) {
  const { t } = useLingui()
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="link-deal-new"
      className="flex items-center gap-[7px] px-px pt-1.5 text-left"
    >
      <PlusIcon width={13} height={13} className="shrink-0 text-[#E0A56A]" />
      <span className="text-[13px] font-medium text-[#E0A56A]">
        <Trans>New deal</Trans>
      </span>
      <span className="text-xs text-[#6E685F]">{t`for ${firstName}`}</span>
    </button>
  )
}

function dealSubtitle(deal: VoiceCallDeal): string {
  return [deal.stage, deal.value].filter(Boolean).join(" · ")
}
